/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import LinkButton from '@atlaskit/button/link';
import { useProductDependencies } from '@atlassian/company-hub-common';

import { LINKS_MENU_EXTENSION_ANALYTIC_SOURCE } from '../../../../../constants';
import type { LinkItemType, Styles } from '../../../../../types';

export type MenuLinkItemProps = {
	index?: number;
	link?: string;
	setWidth?: (width: number, index?: number) => void;
} & Omit<LinkItemType, 'type' | 'link'> &
	Pick<Styles, 'textColor'>;

export const MenuLinkItem = ({
	index,
	itemId,
	link,
	textColor,
	title,
	setWidth,
}: MenuLinkItemProps): React.JSX.Element | null => {
	const ref = useRef<HTMLAnchorElement>(null);
	const { useAnalyticsActions } = useProductDependencies();

	useLayoutEffect(() => {
		if (setWidth && ref.current) {
			setWidth(ref.current.offsetWidth, index);
		}
		// if item title updates, trigger a width recalculation
	}, [setWidth, index, title]);

	const analytics = useAnalyticsActions?.useLinkAnalyticActions?.(link);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredView = useRef(false);

	useEffect(() => {
		if (!hasFiredView.current && analytics?.handleView) {
			analytics.handleView();
			hasFiredView.current = true;
		}
	}, [analytics, analytics?.handleView]);

	const handleLinkButtonClick = () => {
		analytics?.handleClick?.();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link item',
				source: LINKS_MENU_EXTENSION_ANALYTIC_SOURCE,
			},
		}).fire();
	};

	const href = link || undefined;

	return href ? (
		<LinkButton
			appearance="subtle"
			target="_blank"
			href={href}
			id={itemId}
			ref={ref}
			onClick={handleLinkButtonClick}
		>
			<span
				/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
				style={{
					color: textColor,
				}}
				/* eslint-enable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values */
			>
				{title}
			</span>
		</LinkButton>
	) : null;
};
